<template>
  <!-- WeChat connection modal — same headlessui Dialog pattern as ProfileView -->
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="close" class="relative z-50">
      <div class="fixed inset-0 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black/30" />
          </TransitionChild>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div class="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl border border-gray-300">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 text-center">
                {{ t("title") }}
              </DialogTitle>

              <!-- bound state: no QR, status + disconnect -->
              <div v-if="bound" class="mt-4 text-center">
                <div class="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm">
                  ✓ {{ t("boundBadge") }}
                </div>
                <p class="mt-3 text-sm text-gray-600">{{ t("boundDesc") }}</p>
                <p v-if="boundAt" class="mt-1 text-xs text-gray-400">
                  {{ t("boundAt") }} {{ boundAt }}
                </p>
                <div class="mt-5 flex justify-center space-x-3">
                  <button
                    type="button"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    @click="close"
                  >
                    {{ t("close") }}
                  </button>
                  <button
                    type="button"
                    :disabled="disconnecting"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                    @click="disconnect"
                  >
                    {{ disconnecting ? t("disconnecting") : t("disconnect") }}
                  </button>
                </div>
                <p v-if="error" class="mt-2 text-xs text-red-600">{{ error }}</p>
              </div>

              <!-- unbound: description + QR generation -->
              <div v-else class="mt-4">
                <p class="text-sm text-gray-600">{{ t("unboundDesc") }}</p>

                <div v-if="!qrUrl" class="mt-4 text-center">
                  <button
                    type="button"
                    @click="generate"
                    :disabled="loading"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-custom-purple-500 hover:bg-custom-purple-900 rounded-md disabled:opacity-50"
                  >
                    {{ loading ? "…" : t("generateQr") }}
                  </button>
                </div>

                <div v-else class="mt-4 text-center">
                  <img :src="qrUrl" alt="WeChat binding QR" class="mx-auto w-48 h-48 border rounded" />
                  <p class="text-xs text-gray-500 mt-2">{{ t("scanHint") }}</p>
                </div>

                <p v-if="error" class="mt-2 text-xs text-red-600 text-center">{{ error }}</p>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from "@headlessui/vue";
import { createClient } from "@/services/requests.js";
import { getAuthToken } from "@/services/backend-api.js";

// WeChat connect modal: QR binding flow + bound state with disconnect.
// Opened from the user dropdown ("Connecting WeChat"); replaces the
// standalone profile-page card.
export default {
  name: "WechatConnect",
  props: {
    isOpen: { type: Boolean, default: false },
    firmId: { type: String, required: true },
  },
  components: { TransitionRoot, TransitionChild, Dialog, DialogOverlay, DialogTitle },
  data() {
    return {
      bound: false,
      boundAt: null,
      qrUrl: null,
      loading: false,
      disconnecting: false,
      error: null,
    };
  },
  computed: {
    lang() {
      return localStorage.getItem("navLang") || "en";
    },
  },
  created() {
    this.pollTimer = null;
  },
  watch: {
    isOpen(open) {
      if (open) this.checkStatus();
      else this.stopPolling();
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    t(key) {
      const strings = {
        en: {
          title: "Connect WeChat",
          boundBadge: "Connected",
          boundDesc: "Case updates and new messages are pushed to your WeChat via the Patensys official account.",
          boundAt: "Connected on:",
          disconnect: "Disconnect",
          disconnecting: "Disconnecting…",
          close: "Close",
          unboundDesc: "Connect WeChat to receive case messages, filing updates, and deadline reminders in WeChat.",
          generateQr: "Generate binding QR code",
          scanHint: "Scan with WeChat to follow the account and connect (valid 10 min)",
          qrExpired: "QR expired — generate a new one",
        },
        zh: {
          title: "连接微信",
          boundBadge: "已连接",
          boundDesc: "案件动态与新消息将通过 Patensys 公众号推送到您的微信。",
          boundAt: "连接时间:",
          disconnect: "断开连接",
          disconnecting: "断开中…",
          close: "关闭",
          unboundDesc: "连接微信后,案件新消息、文件更新和期限提醒将推送到您的微信。",
          generateQr: "生成绑定二维码",
          scanHint: "使用微信扫一扫,关注公众号并完成连接(10 分钟内有效)",
          qrExpired: "二维码已过期,请重新生成",
        },
      };
      return (strings[this.lang] || strings.en)[key] || key;
    },
    api() {
      return createClient(getAuthToken());
    },
    close() {
      this.stopPolling();
      this.$emit("close");
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async checkStatus() {
      try {
        const res = await this.api().get(
          `/litigation/wechat/bind-status?firm_id=${this.firmId}&_t=${Date.now()}`
        );
        this.bound = !!res.bound;
        if (res.wechatBoundAt?.seconds) {
          this.boundAt = new Date(res.wechatBoundAt.seconds * 1000).toLocaleDateString(
            this.lang == "zh" ? "zh-CN" : "en-US"
          );
        }
        if (this.bound) {
          // scan landed: QR disappears, no second bind
          this.qrUrl = null;
          this.stopPolling();
        }
      } catch (e) {
        console.log("bind-status check failed", e);
      }
    },
    async generate() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api().post(`/litigation/wechat/bind-qr`, {
          firm_id: this.firmId,
        });
        this.qrUrl = res.qrUrl;
        this.pollTimer = setInterval(() => this.checkStatus(), 5000);
        setTimeout(() => {
          if (this.pollTimer) {
            this.stopPolling();
            if (!this.bound) {
              this.qrUrl = null;
              this.error = this.t("qrExpired");
            }
          }
        }, 600000);
      } catch (e) {
        this.error = e.message || "failed";
      } finally {
        this.loading = false;
      }
    },
    async disconnect() {
      this.disconnecting = true;
      this.error = null;
      try {
        await this.api().post(`/litigation/wechat/disconnect`, {
          firm_id: this.firmId,
        });
        this.bound = false;
        this.boundAt = null;
      } catch (e) {
        this.error = e.message || "disconnect failed";
      } finally {
        this.disconnecting = false;
      }
    },
  },
};
</script>
